import { inject, Injectable } from '@angular/core';
import { isNil, Nillable, valueOrUndefined } from '@web/utils';
import { RequiredMethodCallException } from '../../exceptions/required-method-call.exception';
import { CollectionSortRequest } from '../requests/collection-sort.request';
import { CollectionRequest } from '../requests/collection.request';
import { CollectionFilterRequest } from '../requests/collection-filter.request';
import { CollectionSortRequestBuilder } from './collection-sort-request.builder';
import { CollectionFilterRequestBuilder } from './collection-filter-request.builder';
import { CollectionPageSize } from '../types/collection-page-size';

@Injectable({
  providedIn: 'root',
})
export class CollectionRequestBuilder {
  private readonly collectionSortBuilder = inject(CollectionSortRequestBuilder);
  /* prettier-ignore */
  private readonly collectionFilterBuilder = inject<CollectionFilterRequestBuilder<never>>(CollectionFilterRequestBuilder);

  private _filters: Nillable<CollectionFilterRequest[]>;
  private _sorts: Nillable<CollectionSortRequest[]>;
  private _page: Nillable<number>;
  private _limit: Nillable<CollectionPageSize>;
  private _all: Nillable<boolean>;
  private _query: Nillable<string>;

  public constructor() {
    this.reset();
  }

  public newInstance(): CollectionRequestBuilder {
    return new CollectionRequestBuilder();
  }

  public reset(): this {
    this._filters = null;
    this._sorts = null;
    this._page = null;
    this._limit = null;
    this._all = null;
    this._query = null;

    return this;
  }

  public filters<Filter>(
    factory: (
      builder: CollectionFilterRequestBuilder<Filter>,
    ) => CollectionFilterRequest[],
  ): this {
    if (isNil(this._filters)) {
      this._filters = [];
    }

    this._filters.push(...factory(this.collectionFilterBuilder.newInstance()));

    return this;
  }

  public sorts(
    factory: (builder: CollectionSortRequestBuilder) => CollectionSortRequest[],
  ): this {
    if (isNil(this._sorts)) {
      this._sorts = [];
    }

    this._sorts.push(...factory(this.collectionSortBuilder.newInstance()));

    return this;
  }

  public all(all: boolean): this {
    this._all = all;

    return this;
  }

  public page(page: number): this {
    this._page = page;

    return this;
  }

  public pageSize(pageSize: CollectionPageSize): this {
    this._limit = pageSize;

    return this;
  }

  public disablePagination(): this {
    this.all(true);
    this.page(1);

    return this;
  }

  public query(query: Nillable<string>): this {
    this._query = query;

    return this;
  }

  public build(): CollectionRequest {
    this.requirePage();
    this.configureDefaultAll();

    const request = new CollectionRequest(
      this._page!,
      this._limit!,
      this._all!,
      valueOrUndefined(this._filters),
      valueOrUndefined(this._sorts),
      valueOrUndefined(this._query),
    );

    this.reset();

    return request;
  }

  private requirePage(): void {
    if (!isNil(this._page)) {
      return;
    }

    throw new RequiredMethodCallException(this.page.name);
  }

  private configureDefaultAll(): void {
    if (!isNil(this._all)) {
      return;
    }

    this.all(false);
  }
}
